import { generatePath } from 'react-router-dom';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { calculateOffset } from 'utils/helpers';

import { apiQuery } from '../../query';

import {
  PushConfig,
  PushDevice,
  NotificationSetting,
  NotificationRequest,
  PersonalNotification,
  NotificationResponse,
  SavePushDeviceRequest,
  PersonalNotificationsResponse,
  SaveNotificationSettingsRequest,
} from './types';

const tagTypes = ['notifications'];
const pushTags = ['pushDevices'];
const personalTags = ['personalNotifications'];

// WEB PUSH: a 404 (a Main without the push routes yet) must not send the app
// to the Not Found page (api/instance.ts).
const allowNotFound = { 'allow-not-found': 'true' };

export const notificationsApi = createApi({
  baseQuery: apiQuery,
  reducerPath: 'notificationsApi',
  tagTypes: [...tagTypes, ...pushTags, ...personalTags],
  endpoints: (build) => ({
    // deviceId: only that device (the one just turned on).
    sendTestPush: build.mutation<{ devices: number }, { deviceId?: number }>({
      query: (data) => ({ data, method: 'post', url: URL.PUSH_TEST }),
    }),

    pushConfig: build.query<PushConfig, void>({
      query: () => ({
        method: 'get',
        url: URL.PUSH_CONFIG,
        headers: allowNotFound,
      }),
    }),

    markAllUnread: build.mutation<void, void>({
      invalidatesTags: tagTypes,
      query: () => ({
        method: 'post',
        url: URL.MARK_ALL_AS_READ_NOTIFICATIONS,
      }),
    }),

    settings: build.query<NotificationSetting[], void>({
      providesTags: tagTypes,
      query: () => ({
        method: 'get',
        url: URL.GET_NOTIFICATIONS_SETTINGS,
      }),
    }),

    pushDevices: build.query<PushDevice[], void>({
      providesTags: pushTags,
      query: () => ({
        method: 'get',
        url: URL.PUSH_DEVICES,
        headers: allowNotFound,
      }),
    }),

    removePushDevice: build.mutation<void, number>({
      invalidatesTags: pushTags,
      query: (id) => ({
        method: 'delete',
        url: generatePath(URL.PUSH_DEVICE, { id: String(id) }),
      }),
    }),

    markAsRead: build.mutation<void, number>({
      invalidatesTags: tagTypes,
      query: (id) => ({
        method: 'post',
        url: generatePath(URL.MARK_AS_READ_NOTIFICATION, { id: String(id) }),
      }),
    }),

    // PAYOUT SENT (owner, 26 Oct 2026): the signed-in person's own alerts.
    personalNotifications: build.query<PersonalNotificationsResponse, void>({
      providesTags: personalTags,
      query: () => ({
        method: 'get',
        headers: allowNotFound,
        url: URL.PERSONAL_NOTIFICATIONS,
      }),
    }),

    markPersonalRead: build.mutation<PersonalNotification, number>({
      invalidatesTags: personalTags,
      query: (id) => ({
        method: 'post',
        url: generatePath(URL.PERSONAL_NOTIFICATION_READ, { id: String(id) }),
      }),
    }),

    saveSettings: build.mutation<
      NotificationSetting[],
      SaveNotificationSettingsRequest
    >({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.SAVE_NOTIFICATIONS_SETTINGS,
      }),
    }),

    notifications: build.query<
      NotificationResponse,
      NotificationRequest | void
    >({
      providesTags: tagTypes,
      query: (params) => {
        const { page = 1, limit = 20, ...otherParams } = params || {};
        const offset = calculateOffset(page, limit);
        const searchParams = {
          limit,
          offset,
          ...otherParams,
        };

        return {
          method: 'get',
          url: addParamsToUrl(URL.GET_NOTIFICATIONS, searchParams),
        };
      },
    }),
  }),
});

export const {
  useSettingsQuery,
  usePushConfigQuery,
  usePushDevicesQuery,
  useMarkAsReadMutation,
  useNotificationsQuery,
  useSendTestPushMutation,
  useSaveSettingsMutation,
  useMarkAllUnreadMutation,
  useMarkPersonalReadMutation,
  useRemovePushDeviceMutation,
  usePersonalNotificationsQuery,
} = notificationsApi;

export type {
  PushConfig,
  PushDevice,
  NotificationSetting,
  PersonalNotification,
  SavePushDeviceRequest,
  PersonalNotificationsResponse,
  SaveNotificationSettingsRequest,
};
